import { of as observableOf, Subject } from 'rxjs';

import { map, take, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { RequestService } from './requestService';
import { environment } from '../../../environments/environment';
import { UserDocument } from '../classes/userDocument';
import { Application } from '../classes/application';
import { Note } from '../classes/note';
import { HttpClient } from '@angular/common/http';
import { Page } from '../classes/page';
import { UpdateJobProcess } from '../../jobs-module/edit-job-tpl-process/actions/process.actions';
import { Store } from '@ngrx/store';
import { AppState } from '../../app.service';
import { Job } from '../../jobs-module/models/job.model';

export interface RatingModel {
  application_id: string;
  author_id: number;
  created_at: string;
  id: number;
  job_process_step_id: number;
  rate: number;
  updated_at: string;
}

@Injectable()
export class JobService {
  /*
   * ====================================
   *   JOB SERVICE
   * ====================================
   *
   * The job service handles all queries related to a job. This includes:
   *
   *    - Applications
   *    - Questions
   *    - Application Notes
   *    - Category images
   *
   */

  // URL to web api
  private _apiEndpoint = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private _requestService: RequestService,
    private store: Store<AppState>
  ) {}

  private static isUrl(text: string) {
    const expression = /[-a-zA-Z0-9@:%_+.~#?&//=]{2,256}\.[a-z]{2,4}\b(\/[-a-zA-Z0-9@:%_+.~#?&//=]*)?/gi;
    const regex = new RegExp(expression);
    return regex.exec(text);
  }

  public invalidatePagination(path: string) {
    this._requestService.setPaginationPath(path, null);
  }

  /*
   * ====================================
   *   JOB
   * ====================================
   */

  public getJob(job_id) {
    /*
     *
     * This method allows both anonymous and logged in users to
     * request data regarding a single job.
     *
     * Date: 24.06.16
     *
     * When the data is returned, it is parsed to json and casted as a instance of
     * the class Job
     *
     */

    // Returns an observable that maps and casts the result
    return this._requestService.request<Job>(
      'GET',
      this._apiEndpoint + 'jobs/' + job_id + '/'
    );
  }

  public filterJobs(searchterm, company_id, unit_id) {
    let url = this._apiEndpoint + 'jobs/';

    if (searchterm) {
      url += '?search=' + searchterm;
    }
    if (company_id) {
      const prefix = searchterm ? '&' : '?';
      url += prefix + 'company=' + company_id;
    }
    if (unit_id) {
      const prefix = searchterm || company_id ? '&' : '?';
      url += prefix + 'unit=' + unit_id;
    }

    return this._requestService.request<Job[]>('GET', url);
  }

  /*
   * ====================================
   *   UNIT JOBS LIST
   * ====================================
   */

  // TODO fetch all paginated jobs
  public getUnitJobs(unit_id) {
    return this._requestService.request<Page<Job>>(
      'GET',
      this._apiEndpoint + 'units/' + unit_id + '/jobs/?page_size=1000'
    );
  }

  /*
   * ====================================
   *   APPLICATION
   * ====================================
   */

  public getApplications(
    job_id,
    use_pagination,
    status_filter = null,
    getvideointerviewstatus = false,
    applicant_filter = ''
  ) {
    const key = 'applications:' + job_id;

    // Tries to retrieve the next url to call
    let url = this._requestService.getPaginationPath(key);

    if (url === 'empty') {
      return observableOf([]);
    }

    // If it does not exist we use the original
    if (url == null) {
      url = this._apiEndpoint + 'jobs/' + job_id + '/application-list/';
      if (use_pagination) {
        url += '?use_pagination=True';
      }
      if (getvideointerviewstatus) {
        url += '&mobile=true';
      }
    }

    if (status_filter !== null) {
      const prefix = use_pagination ? '&' : '?';
      url += prefix + 'status_filter=' + status_filter;
    }
    if (applicant_filter) {
      const prefix = use_pagination ? '&' : '?';
      url += prefix + 'applicant_filter=' + applicant_filter;
    }

    url += '&use_flat_serializer=True';

    if (use_pagination) {
      // Calls the request service to perform request

      return this._requestService.request<Page<Application>>('GET', url).pipe(
        tap(res => {
          let next = res.next;

          if (next == null) {
            next = 'empty';
          }
          this._requestService.setPaginationPath(key, next);
        }),
        map(res => res.results)
      );
    }

    return this._requestService.request<Application[]>('GET', url);
  }

  public getMessageApplications(
    job_id,
    use_pagination,
    status_filter: number = null,
    getvideointerviewstatus = false,
    applicant_filter = null
  ) {
    const key = 'messageApplications:';

    // Tries to retrieve the next url to call
    let url = this._requestService.getPaginationPath(key);
    if (url === 'empty') {
      return observableOf([]);
    }

    // If it does not exist we use the original
    if (url == null) {
      url = this._apiEndpoint + 'jobs/' + job_id + '/applications/';
      if (use_pagination) {
        url += '?use_pagination=True';
      }
      if (status_filter != null) {
        const prefix = use_pagination ? '&' : '?';
        url += prefix + 'status_filter=' + status_filter;
      }
      if (applicant_filter != null) {
        const prefix = use_pagination || status_filter != null ? '&' : '?';
        url += prefix + 'applicant_filter=' + applicant_filter;
      }
      if (getvideointerviewstatus) {
        const prefix =
          use_pagination || status_filter != null || applicant_filter != null
            ? '&'
            : '?';
        url += prefix + 'mobile=true';
      }
    }

    if (use_pagination) {
      // Calls the request service to perform request
      return this._requestService.request<Page<Application>>('GET', url).pipe(
        tap(res => {
          let next = res.next;

          if (next == null) {
            next = 'empty';
          }

          this._requestService.setPaginationPath(key, next);
        }),
        map(res => res.results)
      );
    }

    return this._requestService.request<Application[]>('GET', url);
  }

  public getMessageApplicationsById(jobId, applicantId) {
    const key = 'messageApplications:';
    // Tries to retrieve the next url to call
    let url = this._requestService.getPaginationPath(key);
    if (url === 'empty') {
      return observableOf([]);
    }
    url = `${this._apiEndpoint}jobs/${jobId}/applications/${applicantId}`;
    return this._requestService.request<Application[]>('GET', url);
  }

  public updateApplication(application) {
    // Makes a copy of object so that all object references is by id
    const copy = Object.assign({}, application);
    copy.applicant = copy.applicant.id;
    copy.job = copy.job.id;

    this._requestService.invalidate('applications:' + copy.job);

    return this._requestService.request<Application>(
      'PUT',
      this._apiEndpoint + 'jobs/' + copy.job + '/applications/' + copy.id + '/',
      '',
      copy
    );
  }

  public checkApplication(job_id) {
    return this._requestService.request<boolean>(
      'GET',
      this._apiEndpoint + 'jobs/' + job_id + '/applications/exists'
    );
  }

  public deleteJob(jobId: number) {
    const res = this._requestService.request(
      'DELETE',
      this._apiEndpoint + 'jobs/' + jobId + '/'
    );
    // Delete cache
    this._requestService.invalidate('assignedJobs');
    return res;
  }

  /*
   * ====================================
   *   TEMPLATES
   * ====================================
   */

  public getTemplates(unitId: number) {
    return this._requestService.request<Page<Job>>(
      'GET',
      this._apiEndpoint + 'units/' + unitId + '/job-templates/?page_size=50'
    );
  }

  public getTemplateByIdAndUnitId(template_id: number, unit_id: number) {
    return this._requestService.request<Job>(
      'GET',
      this._apiEndpoint +
        'units/' +
        unit_id +
        '/job-templates/' +
        template_id +
        '/'
    );
  }

  /*
   * ====================================
   *      VIDEO QUESTION ANSWERS
   * ====================================
   */

  public createVideoInterviewAnswer(application_id, answer) {
    return this._requestService.request(
      'POST',
      this._apiEndpoint +
        'jobs/applications/' +
        application_id +
        '/videointerviewanswers/',
      '',
      answer
    );
  }

  public getVideoInterviewAnswers(application_id) {
    return this._requestService.request(
      'GET',
      this._apiEndpoint +
        'jobs/applications/' +
        application_id +
        '/videointerviewanswers/'
    );
  }

  /*
   * ====================================
   *   APPLICATION NOTES
   * ====================================
   */

  public getApplicationNotes(application_id) {
    return this._requestService.request<Page<Note>>(
      'GET',
      this._apiEndpoint + 'jobs/applications/' + application_id + '/notes/'
    );
  }

  public postApplicationNote(note, application_id) {
    return this._requestService.request<Note>(
      'POST',
      this._apiEndpoint + 'jobs/applications/' + application_id + '/notes/',
      '',
      note
    );
  }

  public getApplication(jobId: number, applicationId: number) {
    const key = 'application:' + applicationId;

    this._requestService.invalidate(key);

    return this._requestService.request<Application>(
      'GET',
      this._apiEndpoint +
        'jobs/' +
        jobId +
        '/applications/' +
        applicationId +
        '/',
      key
    );
  }

  public getApplicantDocuments(applicationId: number) {
    return this._requestService.request<UserDocument[]>(
      'GET',
      this._apiEndpoint + 'jobs/applications/' + applicationId + '/documents/'
    );
  }

  public fetchRating(id) {
    return this._requestService.request<RatingModel[]>(
      'GET',
      this._apiEndpoint + 'ratings/application/' + id + '/application-ratings/'
    );
  }

  public postRating(id, rate) {
    return this._requestService.request<Note>(
      'POST',
      this._apiEndpoint + 'ratings/application/' + id + '/application-ratings/',
      '',
      rate
    );
  }

  public getLeads(id) {
    return this._requestService.request<Page<any>>(
      'GET',
      this._apiEndpoint + 'jobs/' + id + '/applicant-leads/'
    );
  }
}
